import React, {
  useContext,
  useEffect,
  MouseEvent,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import {
  PatientMetaDataProps,
} from 'pages/Dashboard/pages/Encounters/pages/List/types/patientMetaData';
import ToolTip from 'core/components/Tooltip';
import Divider from '@mui/material/Divider';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import IconButton from '@mui/material/IconButton';
import EditIcon from 'core/icons/edit';
import {
  getPCPBlurb,
} from 'utils/misc';
import {
  patientEventChannel,
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import noop from 'lodash/noop';
import EditableAppointmentInsurance from 'views/EMR/components/Insurance/EditableAppointmentInsurance';
import isNil from 'lodash/isNil';
import Tag from 'core/components/Tag';
import {
  formatDateTime,
} from 'utils/date';
import dayjs from 'utils/dayjs';
import AppointmentStatusSelect from 'pages/Dashboard/components/AppointmentStatusSelect';
import {
  useGetMipsSuggestion,
  useGetVisitNoteRelatedInfo,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import Skeleton from 'core/components/Skeleton';
import useSignalREffect from 'views/EMR/hooks/signalR';
import SelectableProvider from 'core/context/selectable';
import MIPS from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MIPS';
import {
  VisitNoteFormBridgeContext,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/contexts/visitNoteFormBridge';
import PatientMedicalBackground from 'pages/Dashboard/pages/Encounters/pages/VisitNote/PatientMedicalBackground';
import PCC from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC';
import VitalsSection from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/Vitals';
import {
  PatientForm,
} from 'pages/Dashboard/types/patient';
import {
  VisitNote,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/types/visitNote';
import {
  AppointmentInfoForVisitNoteDTO,
  EncounterNotificationDTO,
  VisitNoteBillingCodeDTO,
} from 'dtos';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  usePCCAvailability,
} from 'views/EMR/hooks/pcc';

type SectionProps = {
  children: ReactNode;
  readOnly?: boolean;
  title: string;
  onEdit?: (e?: MouseEvent<HTMLElement>) => void;
};

type VisitNoteRelatedInfo = AppointmentInfoForVisitNoteDTO & {
  hasNoPrimaryCarePhysician?: boolean;
};

export function Section({
  children,
  title,
  readOnly = false,
  onEdit = noop,
}: SectionProps) {
  return (
    <div className="flex flex-col text-xs">
      <h4 className="font-semibold text-xs !h-6 flex items-center">
        {title}
      </h4>
      <div className="flex gap-1 items-center font-medium">
        {children}
        {!readOnly && (
          <ToolTip title={`Edit ${title} Information`}>
            <IconButton
              size="small"
              onClick={onEdit}
              color="info"
            >
              <EditIcon className="!w-5 !h-5 !text-inherit" />
            </IconButton>
          </ToolTip>
        )}
      </div>
    </div>
  );
}

export default function Info({ encounter }: PatientMetaDataProps) {
  const { patient } = encounter;
  const { isPCCAvailable } = usePCCAvailability();
  const visitNote = encounter as VisitNote;
  const {
    billingCodes,
    appendBillingCode,
    removeBillingCode,
  } = useContext(VisitNoteFormBridgeContext);
  const {
    encounterId,
    isFinalized = false,
    visitNoteId = 0,
  } = visitNote;

  const {
    data: visitNoteRelatedInfo,
    isLoading: isLoadingRelatedInfo,
    refetch: refetchRelatedInfo,
  } = useGetVisitNoteRelatedInfo(visitNoteId);

  const {
    data: mipsData,
    isLoading: mipsLoading,
    refetch: refetchMipsData,
  } = useGetMipsSuggestion(
    visitNoteId,
    visitNoteId > 0,
  );

  const {
    typeOfVisitDescription,
    visitDateTime,
    appointmentStatus,
    isOutOfPocket,
    insurance,
    primaryCarePhysician,
  } = visitNoteRelatedInfo ?? {};

  const hasNoPrimaryCarePhysician = (visitNoteRelatedInfo as VisitNoteRelatedInfo | undefined)
    ?.hasNoPrimaryCarePhysician;
  const physicianBlurb = hasNoPrimaryCarePhysician
    ? 'None'
    : getPCPBlurb(primaryCarePhysician ?? {});

  const [container, setContainer] = useState<HTMLElement | null>(null);

  const LockIcon = useMemo(() => (
    isFinalized ? LockRoundedIcon : LockOpenRoundedIcon
  ), [isFinalized]);

  useEffect(() => {
    const unsubscribeFromRelatedInfoUpdate = visitNoteEventChannel.on('onRelatedInfoUpdate', ({ patientId }) => {
      if (patientId === Number(patient?.patientId)) {
        refetchRelatedInfo?.();
      }
    });

    const unsubscribeFromSocialHistoryUpdate = patientEventChannel.on('onMedicalBackgroundChange', (patientId, type) => {
      if (patientId === visitNote?.patientId && type === 'socialHistory') {
        refetchMipsData?.();
      }
    });

    return () => {
      unsubscribeFromRelatedInfoUpdate();
      unsubscribeFromSocialHistoryUpdate();
    };
  }, [encounter]);

  useSignalREffect('OnEncounterUpdated', (data: EncounterNotificationDTO) => {
    const { encounterId } = data;
    if (Number(encounter?.encounterId) === encounterId) {
      refetchRelatedInfo?.();
    }
  });

  const selectedInsurance = isOutOfPocket ? 'Out of pocket' : insurance?.insuranceName ?? 'Not Set';

  return (
    <div className="px-4 flex flex-col gap-2">
      <Section
        readOnly
        title={dayjs().isAfter(visitDateTime) ? 'Visited at' : 'Visiting at'}
      >
        {!isLoadingRelatedInfo
          ? formatDateTime(visitDateTime ?? '')
          : <Skeleton className="!w-40" />}
      </Section>
      <Section
        readOnly
        title="Appointment"
      >
        <div className="flex items-center gap-2 w-full">
          <ToolTip title={isFinalized ? 'Visit Note Finalized' : 'Visit Note Not Finalized'}>
            <span className="cursor-pointer">
              <LockIcon className="!w-4 !h-4" />
            </span>
          </ToolTip>
          {!isLoadingRelatedInfo
            ? (
              <>
                <Tag
                  tagName={typeOfVisitDescription ?? '--'}
                  className="flex items-center !inline-block !leading-[20px] !max-w-[160px] small:!max-w-[100px] truncate !h-6"
                  tooltip={typeOfVisitDescription ?? ''}
                />
                <AppointmentStatusSelect
                  encounterId={encounterId}
                  value={appointmentStatus ?? ''}
                  appointmentDate={visitDateTime}
                  onUpdate={async () => { await refetchRelatedInfo(); }}
                  disabled={isFinalized}
                  classes={{
                    root: '!w-fit',
                    label: 'text-gray-700 text-xs',
                    select: '!py-[2px] rounded',
                  }}
                />
              </>
            )
            : (
              <>
                <Skeleton className="!w-20" />
                <Skeleton className="!w-40" />
              </>
            )}
        </div>
      </Section>
      <Section
        title="Insurance"
        onEdit={(e) => {
          setContainer(e?.currentTarget ?? null);
        }}
      >
        {isLoadingRelatedInfo ? <Skeleton className="!w-40" /> : selectedInsurance}
      </Section>
      <Section
        title="Primary Care Physician"
        onEdit={() => {
          patientEventChannel.emit('onEditPCP', {
            primaryCarePhysician: primaryCarePhysician ?? {},
            hasNoPrimaryCarePhysician,
            patient: patient as PatientCompact,
          });
        }}
      >
        {isLoadingRelatedInfo ? <Skeleton className="!w-40" /> : physicianBlurb}
      </Section>
      <VitalsSection
        patientId={patient?.patientId ?? 0}
        encounterId={encounterId}
        readOnly={isFinalized}
      />
      <Divider flexItem />
      <PatientMedicalBackground {...visitNote} />
      <SelectableProvider>
        <MIPS
          selectedMips={visitNote?.bill?.relatedMipsList ?? []}
          relatedMipsList={mipsData ?? []}
          patient={visitNote?.patient}
          isLoading={mipsLoading}
          billingCodes={(billingCodes ?? []) as VisitNoteBillingCodeDTO[]}
          appendBillingCode={appendBillingCode}
          removeBillingCode={removeBillingCode}
        />
      </SelectableProvider>
      {isPCCAvailable && (
        <>
          <Divider flexItem />
          <PCC
            patient={patient as PatientForm}
            viewMode="compact"
            disabled={isFinalized}
          />
        </>
      )}
      {!isNil(container) && (
        <EditableAppointmentInsurance
          patientId={patient?.patientId ?? 0}
          encounterId={encounterId}
          isOutOfPocket={isOutOfPocket}
          insuranceId={insurance?.insuranceId ?? 0}
          classes={{ paper: '!left-auto !right-3' }}
          container={container}
          onClose={() => { setContainer(null); }}
          alwaysEditable
        />
      )}
    </div>
  );
}
